/**
 * Evaluator for `HMIFieldRule` cross-field dependency/validation rules (see
 * `types.ts`). Deliberately structured data (not a parsed string expression
 * language, unlike `enabled_when` in `expression.ts`) so every rule a user
 * builds in the layout builder's "Rules" tab is guaranteed well-formed -
 * there is no free-text grammar to mis-author or mis-parse.
 */
import { HMIField, HMIFieldRule, HMILiveValues, HMIRuleClause, HMIRuleOperand, HMIRuleOperator } from './types';

function resolveOperand(operand: HMIRuleOperand, values: HMILiveValues): unknown {
  if (operand.kind === 'value') return operand.value;
  return values[operand.fieldId];
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value;
  if (typeof value === 'boolean') return undefined;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return undefined;
}

/**
 * Evaluates one `field <operator> literal-or-other-field` clause against the
 * current/candidate values. Relational operators (`>`, `>=`, `<`, `<=`) fail
 * open (return `true`, i.e. "no violation") when either side isn't numeric
 * yet - e.g. the other field hasn't been set - so a rule never hard-locks a
 * commit purely because of missing data elsewhere.
 */
export function evaluateClause(clause: HMIRuleClause, values: HMILiveValues): boolean {
  const left = values[clause.fieldId];
  const right = resolveOperand(clause.compareTo, values);

  if (clause.operator === '==') return looseEquals(left, right);
  if (clause.operator === '!=') return !looseEquals(left, right);

  const l = toNumber(left);
  const r = toNumber(right);
  if (l === undefined || r === undefined) return true;

  switch (clause.operator) {
    case '>':
      return l > r;
    case '>=':
      return l >= r;
    case '<':
      return l < r;
    case '<=':
      return l <= r;
    default:
      return true;
  }
}

function looseEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  const an = toNumber(a);
  const bn = toNumber(b);
  if (an !== undefined && bn !== undefined) return an === bn;
  return String(a ?? '') === String(b ?? '');
}

/** Every field id a rule reads from, used to decide when it needs re-checking. */
export function ruleFieldIds(rule: HMIFieldRule): string[] {
  const ids = new Set<string>();
  const addClause = (clause: HMIRuleClause) => {
    ids.add(clause.fieldId);
    if (clause.compareTo.kind === 'field') ids.add(clause.compareTo.fieldId);
  };
  (rule.when ?? []).forEach(addClause);
  addClause(rule.constraint);
  return Array.from(ids);
}

export function ruleReferencesField(rule: HMIFieldRule, fieldId: string): boolean {
  return ruleFieldIds(rule).includes(fieldId);
}

function fieldCaption(fields: HMIField[], fieldId: string): string {
  return fields.find((f) => f.id === fieldId)?.caption ?? fieldId;
}

function operandLabel(fields: HMIField[], operand: HMIRuleOperand): string {
  if (operand.kind === 'field') return fieldCaption(fields, operand.fieldId);
  return String(operand.value);
}

function operatorLabel(operator: HMIRuleOperator): string {
  const labels: Record<HMIRuleOperator, string> = {
    '==': 'must equal',
    '!=': 'must not equal',
    '>': 'must be greater than',
    '>=': 'must be at least',
    '<': 'must be less than',
    '<=': 'must be at most',
  };
  return labels[operator];
}

/** Human-readable description of a rule, used as the validation error when no custom `message` is set. */
export function describeRule(rule: HMIFieldRule, fields: HMIField[]): string {
  const constraintText = `${fieldCaption(fields, rule.constraint.fieldId)} ${operatorLabel(
    rule.constraint.operator
  )} ${operandLabel(fields, rule.constraint.compareTo)}`;
  if (!rule.when || rule.when.length === 0) return constraintText;
  const whenText = rule.when
    .map(
      (clause) =>
        `${fieldCaption(fields, clause.fieldId)} ${operatorLabel(clause.operator)} ${operandLabel(
          fields,
          clause.compareTo
        )}`
    )
    .join(' and ');
  return `When ${whenText}, then ${constraintText}`;
}

/**
 * Checks every rule that reads `changedFieldId` against `values` (which
 * should already include the candidate new value for that field), and
 * returns the first violation's message, or `undefined` if every applicable
 * rule is satisfied.
 */
export function validateFieldRules(
  rules: HMIFieldRule[] | undefined,
  fields: HMIField[],
  values: HMILiveValues,
  changedFieldId: string
): string | undefined {
  if (!rules || rules.length === 0) return undefined;

  const violated = rules.find((rule) => {
    if (!ruleFieldIds(rule).includes(changedFieldId)) return false;
    const whenPasses = (rule.when ?? []).every((clause) => evaluateClause(clause, values));
    if (!whenPasses) return false;
    return !evaluateClause(rule.constraint, values);
  });

  return violated ? violated.message?.trim() || describeRule(violated, fields) : undefined;
}
